import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { TextBox, TextArea, NumberBox, DateBox, CheckBox } from 'devextreme-react';

import AntAPI from '../utils/AntAPI';
import AntTime from '../utils/AntTime';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';
import { buildLookupPatch } from '../utils/AntGridInlineEdit';
import AntDropDownGrid from './AntDropDownGrid';
import AntSelectBox from './AntSelectBox';
import AntTagBox from './AntTagBox';

const NUMBER_TYPES = ['int', 'float', 'decimal', 'fixedPoint', 'number', 'currency', 'percent'];
const DATE_TYPES = ['date', 'datetime', 'timestamp'];

// Gộp một tuỳ chọn lookup theo đúng thứ tự ưu tiên của AntBaseForm:
// item.options → editor config (editorName, vd EXPItem_All) → config/fields của bảng.
const pick = (sources, name, fallback) => {
    for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        if (source && source[name] !== undefined && source[name] !== null) return source[name];
    }
    return fallback;
};

// Resolve editor cho một field addForm như AntBaseForm.initFormItem.
export const resolveInlineEditor = (item, config, tableName, row) => {
    const fieldConfig = config && config.getCnfField ? config.getCnfField(item.dataField, tableName) : null;
    let editorName = item.editorName;
    let editorType = item.editorType;
    if (editorName && typeof editorName === 'object') editorName = AntCondition.getValue(row, editorName);
    if (editorType && typeof editorType === 'object') editorType = AntCondition.getValue(row, editorType);
    let editorConfig = editorName && config && config.getCnfEditor ? config.getCnfEditor(editorName) : null;
    if (editorConfig) editorType = editorConfig.type || editorType;
    if (!editorType && fieldConfig && fieldConfig.editorType) editorType = fieldConfig.editorType;
    if (!editorConfig && !editorName && fieldConfig && fieldConfig.editorName && config.getCnfEditor) {
        editorConfig = config.getCnfEditor(fieldConfig.editorName);
        if (editorConfig) editorType = editorConfig.type || editorType;
    }
    if (!editorType) {
        const values = (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values) || (item.options && item.options.values);
        if (values instanceof Array) editorType = 'antSelectBox';
    }
    // Khoá ngoại không có editorName (lưới không cấu hình form): tìm editor danh mục theo quy ước
    // <Bảng>_All / <Bảng>_Open; không có thì dựng ô chọn trực tiếp theo bảng danh mục.
    let fallbackLookup = null;
    const lookupTable = item._lookupTable || (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.table);
    const isRemoteLookup = Boolean(lookupTable) || Boolean(fieldConfig && fieldConfig.lookup && fieldConfig.lookup.isRemote);
    if (!editorType && isRemoteLookup && lookupTable) {
        const conventional = config && config.getCnfEditor ? config.getCnfEditor(lookupTable + '_All') || config.getCnfEditor(lookupTable + '_Open') : null;
        if (conventional) {
            editorConfig = conventional;
            editorType = conventional.type || 'antDropDownGrid';
        } else {
            const services = config && config.appGlobal && config.appGlobal.appConfig && config.appGlobal.appConfig.Services;
            const service = services instanceof Array ? services.find((sv) => sv && sv.name === lookupTable) : null;
            if (service && service.module) {
                editorType = 'antDropDownGrid';
                fallbackLookup = {
                    module: service.module,
                    table: lookupTable,
                    isRemote: true,
                    valueField: 'code',
                    displayField: 'name',
                    columns: [{ dataField: 'code' }, { dataField: 'name' }]
                };
            }
        }
    }
    const dataType = item.dataType || (fieldConfig && fieldConfig.dataType) || (editorConfig && editorConfig.dataType);
    if (!editorType && dataType === 'Array') editorType = 'antTagBox';
    return { fieldConfig, editorConfig, editorType, dataType, fallbackLookup };
};

const numberFormat = (dataType, item) => {
    if (dataType === 'currency') return AntLocalization.getCurrencyFormat();
    if (dataType === 'percent') return '#0.##%';
    if (dataType === 'int') return '#,##0';
    const digits = item && typeof item.decimalDigit === 'number' ? item.decimalDigit : 2;
    return digits > 0 ? '#,##0.' + '#'.repeat(digits) : '#,##0';
};

const MIN_MULTILINE_HEIGHT = 32;
const MIN_RESIZE_WIDTH = 60;

// Ô nhập nhiều dòng:
//  - mở ra cao bằng dòng lúc CHƯA sửa (rowHeight do AntGrid đo trước khi dựng editor), nội dung dài hơn thì giãn theo;
//  - gõ thêm dòng thì tự giãn; nút kéo góc dưới phải đổi chiều cao (dòng cao theo) và độ rộng cột (lưu như kéo tiêu đề cột).
function MultilineCellEditor(props) {
    const { value, rowHeight, onResizeColumn, onClose, onInitialized, onKeyDown, onValueChanged } = props;
    const wrapRef = useRef();
    const gripRef = useRef();
    const instance = useRef(null);
    // Chiều cao người dùng kéo: không tự co nhỏ hơn mức này khi gõ.
    const userHeight = useRef(0);
    const baseHeight = Math.max(MIN_MULTILINE_HEIGHT, typeof rowHeight === 'number' && rowHeight > 0 ? rowHeight - 1 : 0);

    const widgetElement = () => wrapRef.current && wrapRef.current.querySelector('.dx-textarea');
    const setHeight = (height) => {
        const widget = widgetElement();
        if (widget) widget.style.height = Math.round(height) + 'px';
    };
    // Nội dung dài hơn khung thì giãn khung vừa đủ (không co lại dưới mức nền / mức người dùng kéo).
    const fitContent = useCallback(() => {
        const widget = widgetElement();
        const input = widget && widget.querySelector('textarea');
        if (!widget || !input) return;
        const floor = Math.max(baseHeight, userHeight.current);
        const extra = input.scrollHeight - input.clientHeight;
        const current = widget.offsetHeight;
        if (extra > 0) setHeight(current + extra);
        else if (current < floor) setHeight(floor);
    }, [baseHeight]);

    useEffect(() => {
        setHeight(baseHeight);
        fitContent();
    }, []);

    // Enter trong ô nhiều dòng = xuống dòng. DevExtreme bắt Enter ở lưới (đóng ô, chuyển sang ô khác) nên chặn
    // lan lên từ khung editor; Ctrl/Cmd+Enter vẫn để lưới xử lý (lưu và rời ô như cũ).
    useEffect(() => {
        const wrapper = wrapRef.current;
        if (!wrapper) return undefined;
        const onEnter = (e) => {
            if (e.key !== 'Enter' || e.ctrlKey || e.metaKey || e.altKey) return;
            e.stopPropagation();
        };
        wrapper.addEventListener('keydown', onEnter);
        return () => wrapper.removeEventListener('keydown', onEnter);
    }, []);

    // Đóng editor: chạy phần phân bổ lại cột đã hoãn trong lúc kéo (AntGrid).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(
        () => () => {
            if (onCloseRef.current) setTimeout(onCloseRef.current, 0);
        },
        []
    );

    // Kéo góc: bắt pointerdown trực tiếp trên nút (pha target) để lưới không coi là bấm ra ngoài/đổi dòng.
    useEffect(() => {
        const grip = gripRef.current;
        if (!grip) return undefined;
        let drag = null;
        let frame = 0;
        const move = (e) => {
            if (!drag) return;
            e.preventDefault();
            const height = Math.max(MIN_MULTILINE_HEIGHT, drag.height + e.clientY - drag.y);
            userHeight.current = height;
            setHeight(height);
            if (!onResizeColumn) return;
            drag.width = Math.max(MIN_RESIZE_WIDTH, Math.round(drag.startWidth + e.clientX - drag.x));
            if (!frame)
                frame = requestAnimationFrame(() => {
                    frame = 0;
                    if (drag) onResizeColumn(drag.width, false);
                });
        };
        const up = () => {
            if (!drag) return;
            const done = drag;
            drag = null;
            if (frame) cancelAnimationFrame(frame);
            frame = 0;
            document.removeEventListener('pointermove', move, true);
            document.removeEventListener('pointerup', up, true);
            document.removeEventListener('pointercancel', up, true);
            if (onResizeColumn && Math.abs(done.width - done.startWidth) >= 1) onResizeColumn(done.width, true);
            fitContent();
            if (instance.current && instance.current.focus) instance.current.focus();
        };
        const down = (e) => {
            if (e.button !== undefined && e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            const widget = widgetElement();
            const cell = wrapRef.current && wrapRef.current.closest('td');
            const startWidth = cell ? cell.offsetWidth : 0;
            drag = { x: e.clientX, y: e.clientY, height: widget ? widget.offsetHeight : baseHeight, startWidth, width: startWidth };
            document.addEventListener('pointermove', move, true);
            document.addEventListener('pointerup', up, true);
            document.addEventListener('pointercancel', up, true);
        };
        const stop = (e) => e.stopPropagation();
        grip.addEventListener('pointerdown', down);
        grip.addEventListener('mousedown', stop);
        grip.addEventListener('click', stop);
        grip.addEventListener('dblclick', stop);
        return () => {
            grip.removeEventListener('pointerdown', down);
            grip.removeEventListener('mousedown', stop);
            grip.removeEventListener('click', stop);
            grip.removeEventListener('dblclick', stop);
            up();
        };
    }, [onResizeColumn, fitContent, baseHeight]);

    return (
        <div ref={wrapRef} className="ant-grid-cell-editor ant-grid-cell-editor-multiline">
            <TextArea
                defaultValue={value === undefined || value === null ? '' : String(value)}
                valueChangeEvent="change"
                onInitialized={(e) => {
                    instance.current = e && e.component;
                    if (onInitialized) onInitialized(e);
                }}
                onKeyDown={onKeyDown}
                onInput={fitContent}
                onValueChanged={onValueChanged}
            />
            <span ref={gripRef} className="ant-grid-cell-editor-grip" title={AntLocalization.translateText({ vi: 'Kéo để đổi kích thước', en: 'Drag to resize', fr: 'Glisser pour ajuster', es: 'Arrastre para ajustar', id: 'Seret untuk ubah ukuran', ja: 'ドラッグでサイズ変更', zh: '拖动调整大小', ko: '드래그하여 크기 조정', hi: 'आकार बदलने हेतु खींचें', ar: 'اسحب لتغيير الحجم' })} />
        </div>
    );
}

// Editor trong ô cho lưới nhập trực tiếp. Không đi qua luồng setValue của DevExtreme vì một lần
// chọn danh mục phải ghi nhiều field (item_code + item_name + mapping); gọi onCommit(patch).
export default function AntGridCellEditor(props) {
    const { item, config, tableName, row, prepareLookups, onCommit, onCancel } = props;
    const committed = useRef(false);
    const wrapRef = useRef();
    const editorInstance = useRef(null);
    const wrap = (node) => (
        <div ref={wrapRef} className="ant-grid-cell-editor">
            {node}
        </div>
    );

    // Khớp chiều cao ô: dòng cao lên vì cột nhiều dòng thì editor một dòng kéo cao bằng ô (chữ canh
    // giữa), ô nhiều dòng lấy chiều cao ô làm tối thiểu và vẫn tự giãn khi gõ dài.
    // Ô nhiều dòng tự lo chiều cao (MultilineCellEditor).
    useEffect(() => {
        if (props.multiline || item.editorType === 'textArea') return;
        const wrapper = wrapRef.current;
        const cell = wrapper && wrapper.closest('td');
        if (!cell) return;
        const height = cell.clientHeight;
        if (height > 0) wrapper.style.height = height + 'px';
    }, []);

    // Mở ô (bấm hoặc Tab tới) là gõ được ngay — kể cả ô chọn danh mục vốn không tự nhận focus.
    useEffect(() => {
        const timer = setTimeout(() => {
            const input = wrapRef.current && wrapRef.current.querySelector('input:not([type=hidden]), textarea');
            if (input && document.activeElement !== input) {
                try {
                    input.focus();
                } catch (e) {
                    // editor đã huỷ
                }
            }
        }, 30);
        return () => clearTimeout(timer);
    }, []);

    const resolved = useMemo(() => resolveInlineEditor(item, config, tableName, row), [item, config, tableName, row]);
    const { fieldConfig, editorConfig, editorType, dataType, fallbackLookup } = resolved;
    const dataField = item.dataField;
    const value = row ? row[dataField] : undefined;

    // opts.close: đóng ô sau khi chọn danh mục; ô chữ/số lưu lúc rời ô nên để DevExtreme tự chuyển ô (Tab/Enter).
    const commit = useCallback(
        (patch, opts) => {
            // Ô tag chọn nhiều lần trong cùng một lần mở: mỗi lần đổi đều lưu (repeatable).
            if (committed.current && !(opts && opts.repeatable)) return;
            committed.current = true;
            if (onCommit) onCommit(patch, opts);
        },
        [onCommit]
    );

    const focusOnInit = useCallback((e) => {
        if (e && e.component) editorInstance.current = e.component;
        setTimeout(() => {
            try {
                if (e && e.component && e.component.focus) e.component.focus();
            } catch (err) {
                // editor đã huỷ
            }
        });
    }, []);

    // Ô số / tiền: vào ô là bôi đen cả giá trị — gõ đè ngay, không phải xoá số cũ. Chờ 1 nhịp: NumberBox định dạng lại
    // chữ và đặt con trỏ khi nhận focus, chọn ngay sẽ bị bỏ chọn.
    const selectAllOnFocus = useCallback((e) => {
        const input = e && e.element && e.element.querySelector ? e.element.querySelector('input:not([type=hidden])') : null;
        if (!input) return;
        setTimeout(() => {
            try {
                if (document.activeElement === input) input.select();
            } catch (err) {
                // editor đã huỷ
            }
        }, 0);
    }, []);

    const onKeyDown = useCallback(
        (e) => {
            const key = e && e.event && e.event.key;
            if (key === 'Escape' && onCancel) onCancel();
        },
        [onCancel]
    );

    const lookupSources = [item.options, fallbackLookup, editorConfig && editorConfig.lookup, fieldConfig && fieldConfig.lookup];
    const isLookup = ['antDropDownGrid', 'antSelectBox', 'antTagBox'].includes(editorType);

    if (isLookup) {
        const url = pick(lookupSources, 'url', null);
        const isRemote = pick(lookupSources, 'isRemote', Boolean(url));
        const valueField = pick(lookupSources, 'valueField', 'code');
        const displayField = pick(lookupSources, 'displayField', 'name');
        let formDisplayField = pick(lookupSources, 'formDisplayField', null);
        if (!formDisplayField && String(dataField).endsWith('_code')) formDisplayField = String(dataField).replace(/_code$/, '_name');
        const moduleName = pick(lookupSources, 'module', null);
        const lkTable = pick(lookupSources, 'table', null);
        const serverName = pick([item.options && { serverName: item.options.lookupServerName }, editorConfig && editorConfig.lookup, fieldConfig && fieldConfig.lookup], 'serverName', dataField);
        const mappingWhen = pick(lookupSources, 'mappingWhen', null);
        let mappings = pick(lookupSources, 'mapping', []);
        if (mappingWhen && typeof mappingWhen === 'object' && !AntCondition.isValid(row, { when: mappingWhen, whenType: pick(lookupSources, 'mappingWhenType', undefined) }))
            mappings = [];
        mappings = (mappings instanceof Array ? mappings : []).filter((m) => !m || !m.when || AntCondition.isValid(row, { when: m.when, whenType: m.whenType }));

        let dataSource = [];
        if (!isRemote) {
            // Cùng cách form popup (AntBaseForm antSelectBox): danh mục cấu hình + danh mục BE trả (prepare); mục BE ẩn
            // (visible: false) bỏ; có danh mục cấu hình thì chỉ giữ mã có trong đó; mọi mục xét điều kiện hiển thị theo dòng.
            const values = pick(lookupSources, 'values', null);
            const clientValues = (values instanceof Array ? values : []).filter(Boolean).map((v) => (typeof v === 'string' ? { code: v } : v));
            dataSource = clientValues.map((v) => ({ ...v }));
            const found = (prepareLookups instanceof Array ? prepareLookups : []).find((l) => l && l.name === serverName);
            if (found && found.values instanceof Array)
                found.values.forEach((raw) => {
                    if (!raw) return;
                    const v = typeof raw === 'object' ? raw : { code: raw };
                    if (v.visible === false) return;
                    const existing = v.code ? dataSource.find((d) => d.code === v.code) : null;
                    if (existing) {
                        Object.keys(v).forEach((k) => {
                            if (existing[k] === undefined) existing[k] = v[k];
                        });
                    } else dataSource.push({ ...v });
                });
            if (clientValues.length) dataSource = dataSource.filter((v) => clientValues.some((d) => d.code === v.code));
            dataSource = dataSource.filter((v) => AntCondition.isVisible(row, v));
            const searchConditions = pick(lookupSources, 'searchConditions', null);
            if (searchConditions) dataSource = dataSource.filter((v) => AntCondition.isValid(v, { when: { ...searchConditions } }));
        }

        const API = moduleName && lkTable ? new AntAPI(moduleName, lkTable) : props.API;
        const common = {
            dataField,
            name: (lkTable || tableName) + '_inline_' + dataField,
            formDisplayField,
            API,
            url,
            isRemote,
            remotePaging: pick(lookupSources, 'remotePaging', true),
            refreshOnOpened: pick(lookupSources, 'refreshOnOpened', undefined),
            // Lọc theo field khác (vd hàng theo người bán): ô nhập trực tiếp không có form → lấy giá trị từ dòng.
            parentFields: pick(lookupSources, 'parentFields', undefined),
            parentData: row,
            lookupName: serverName,
            searchConditions: pick(lookupSources, 'searchConditions', null),
            acceptCustomValue: pick(lookupSources, 'allowNew', false),
            showClearButton: true,
            focusStateEnabled: true,
            onFocusIn: undefined
        };
        const onLookupChanged = (e) => {
            let next = e ? e.value : null;
            if (editorType === 'antTagBox' && next instanceof Array && next.length === 0) next = null;
            commit(
                editorType === 'antTagBox'
                    ? { [dataField]: next }
                    : buildLookupPatch({ dataField, value: next, rowData: e && e.rowData, formDisplayField, displayField, mappings, currentRow: row }),
                editorType === 'antTagBox' ? { repeatable: true } : { close: true }
            );
        };

        if (editorType === 'antDropDownGrid') {
            return wrap(
                <AntDropDownGrid
                    {...common}
                    settingsName={editorConfig && editorConfig.name ? editorConfig.name : undefined}
                    value={value}
                    height={pick(lookupSources, 'height', 250)}
                    width={pick(lookupSources, 'width', undefined)}
                    ordering={pick(lookupSources, 'ordering', null)}
                    isSearching={true}
                    showDropDownButton={false}
                    gridOptions={{
                        ...pick([item.options, editorConfig && editorConfig.lookup, fieldConfig && fieldConfig.lookup], 'dropDownOptions', {}),
                        ...(fieldConfig && fieldConfig.lookup),
                        ...(editorConfig && editorConfig.lookup),
                        ...item.options,
                        module: moduleName,
                        table: lkTable,
                        dataSource,
                        keyExpr: pick(lookupSources, 'keyField', null),
                        valueExpr: valueField,
                        displayExpr: displayField,
                        columns: pick(lookupSources, 'columns', isRemote ? [{ dataField }] : [{ dataField: 'name' }]),
                        searchBar: isRemote ? pick(lookupSources, 'searchBar', null) : false,
                        showActions: pick(lookupSources, 'showActions', true),
                        actionItems: pick(lookupSources, 'actionItems', undefined)
                    }}
                    getQueryFields={() => mappings.map((m) => m && m.from).filter(Boolean)}
                    onValueChanged={onLookupChanged}
                />
            );
        }
        const selectOptions = {
            ...item.options,
            table: lkTable,
            dataSource,
            keyExpr: pick(lookupSources, 'keyField', editorType === 'antTagBox' ? 'code' : null),
            valueExpr: valueField,
            displayExpr: displayField
        };
        if (editorType === 'antTagBox')
            return wrap(<AntTagBox {...common} defaultValue={value} options={selectOptions} onValueChanged={onLookupChanged} />);
        return wrap(
            <AntSelectBox
                {...common}
                defaultValue={value}
                options={selectOptions}
                searchEnabled={Boolean(isRemote)}
                getQueryFields={() => mappings.map((m) => m && m.from).filter(Boolean)}
                onValueChanged={onLookupChanged}
            />
        );
    }

    if (dataType === 'boolean' || dataType === 'bool') {
        return wrap(
            <CheckBox
                defaultValue={Boolean(value)}
                onInitialized={focusOnInit}
                onValueChanged={(e) => commit({ [dataField]: e.value ? 1 : 0 }, { close: true })}
            />
        );
    }

    if (DATE_TYPES.includes(dataType)) {
        return wrap(
            <DateBox
                type={dataType === 'date' ? 'date' : 'datetime'}
                displayFormat={dataType === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm'}
                defaultValue={typeof value === 'number' ? AntTime.fromUnix(value).toDate() : null}
                showClearButton={true}
                onInitialized={focusOnInit}
                onKeyDown={onKeyDown}
                onValueChanged={(e) => commit({ [dataField]: e.value ? AntTime.init(e.value).unix() : null })}
            />
        );
    }

    if (NUMBER_TYPES.includes(dataType)) {
        return wrap(
            <NumberBox
                defaultValue={value === undefined || value === null || value === '' ? null : Number(value)}
                format={numberFormat(dataType, item)}
                valueChangeEvent="change"
                className="ant-align-right-editor"
                onInitialized={focusOnInit}
                onFocusIn={selectAllOnFocus}
                onKeyDown={onKeyDown}
                onValueChanged={(e) => commit({ [dataField]: e.value })}
            />
        );
    }

    // Nhiều dòng (textArea trong addForm/config, hoặc cột multiline): ô tự giãn chiều cao.
    if (props.multiline || item.editorType === 'textArea') {
        return (
            <MultilineCellEditor
                value={value}
                rowHeight={props.rowHeight}
                onResizeColumn={props.onResizeColumn}
                onClose={props.onClose}
                onInitialized={focusOnInit}
                onKeyDown={onKeyDown}
                onValueChanged={(e) => commit({ [dataField]: e.value === '' ? null : e.value })}
            />
        );
    }

    return wrap(
        <TextBox
            defaultValue={value === undefined || value === null ? '' : String(value)}
            valueChangeEvent="change"
            onInitialized={focusOnInit}
            onKeyDown={onKeyDown}
            onValueChanged={(e) => commit({ [dataField]: e.value === '' ? null : e.value })}
        />
    );
}
